import logger, { installAppActionBreadcrumbs, truncateAppActionValue } from '@goldcast/logger';
import { AnalyticsBrowser } from '@segment/analytics-next';

import { getEnv } from './utils/env';

import { sessionService } from '@/services/sessionService';
import store from '@/store';
import { getCurrentDeviceType } from '@/utils/deviceType';

const analytics: AnalyticsBrowser = AnalyticsBrowser.load(
  {
    writeKey: getEnv('VUE_APP_SEGMENT_KEY') || '',
    cdnURL: getEnv('VUE_APP_ANALYTICS_CDN_URL') || ''
  },
  {
    integrations: getEnv('VUE_APP_SEGMENT_INTEGRATION_CONFIG') as any
  }
);

const originalTrack = analytics.track;
const originalPage = analytics.page;

// Routes that never authenticate. A Segment event here mints an anonymous MTU
// for a session that can never become a known user, so it is pure cost. Auth
// and signup pages are deliberately NOT listed: those sessions do go on to log
// in, and their funnel tracking is worth keeping.
//
// MTU is billed per unique anonymousId, not per event, so a route only stops
// costing anything once it emits zero events — hence blocking at the source
// rather than thinning the volume.
const NEVER_AUTHENTICATED_PATHS = ['/shared/clip', '/shared/post', '/registrants-activity', '/on-demand-embed-dummy'];

// Read from location rather than the router: router/index.js imports this
// module, so importing it back would be circular.
const isNeverAuthenticatedRoute = (): boolean =>
  NEVER_AUTHENTICATED_PATHS.some(path => window.location?.pathname?.startsWith(path));

analytics.track = (...args) => {
  const currentUser = store?.getters['user/getCurrentUser'];
  const eventName = args[0] as string;
  const props = {
    ...args[1],
    email: currentUser?.email,
    is_content_lab_standalone: currentUser?.is_content_lab_standalone,
    is_free_trial: store?.getters['org/getCurrentOrg']?.is_free_trial,
    email_verified: store?.getters['user/getIsCurrentUserEmailVerified']
  };

  if (store?.getters['org/getCurrentOrg']?.useLogrocketTrack) {
    logger.track(eventName, props);
  }

  if (getEnv('VUE_APP_AMPLITUDE_ENABLED') === 'true') {
    logger.trackAmplitude(eventName, props);
  }

  if (isNeverAuthenticatedRoute()) {
    return Promise.resolve() as unknown as ReturnType<typeof originalTrack>;
  }

  return originalTrack(eventName, props);
};

// page() bypasses the track wrapper, and router.afterEach fires it on every
// navigation — including the public routes above.
analytics.page = ((...args: any[]) => {
  if (isNeverAuthenticatedRoute()) {
    return Promise.resolve();
  }
  return (originalPage as any)(...args);
}) as typeof analytics.page;

(window.analytics as unknown as AnalyticsBrowser) = analytics;

// Install delegated capture-phase listeners for app_action breadcrumbs.
// Non-blocking, safe (no preventDefault/stopPropagation), and coalesced.
installAppActionBreadcrumbs(logger);

const logActionBreadcrumb = (action: string, target?: string) => {
  try {
    logger.info('[analytics] app_action', {
      action,
      ...(target && { target }),
      route: truncateAppActionValue(window.location?.pathname || '')
    });
  } catch {
    // Breadcrumb logging must never change the existing analytics behavior.
  }
};

const trackEvent = (eventName: string, eventProperties: { [key: string]: any }) => {
  logActionBreadcrumb(eventName, eventProperties.button_name);
  analytics.track(eventName, eventProperties);
};

export function trackMouseClick(e: MouseEvent) {
  const target = e.target as HTMLElement;

  if (!target) {
    return;
  }

  const closestButton = target.closest('button');
  if (closestButton?.dataset?.testid) {
    trackEvent('button_click', { button_name: closestButton.dataset.testid });
  }

  const closestLink = target.closest('a');
  if (closestLink?.dataset?.testid) {
    trackEvent('link_click', { button_name: closestLink.dataset.testid });
  }
}

export const trackLoginOrLogoutEvent = ({
  eventType,
  isSuccess,
  loginMethod
}: {
  eventType: 'Login' | 'Logout';
  isSuccess: boolean;
  loginMethod?: string;
}) => {
  const payload = {
    device_type: getCurrentDeviceType(),
    action: eventType,
    is_success: isSuccess,
    session_id: sessionService.getSessionId(),
    ...(loginMethod && eventType === 'Login' && { login_method: loginMethod })
  };
  logActionBreadcrumb('studioLoginLogout', eventType);
  analytics.track('studioLoginLogout', payload);
};

export default analytics;
